import React, { useEffect } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'

import { DatePicker, FileInput, Input, Select } from '@/components/Form'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

import {
  DOCUMENT_TEMPLATE_FILE_ACCEPT,
  DOCUMENT_TEMPLATE_TYPE_OPTIONS,
  DocumentTemplateTemplateType,
  DocumentTemplateType,
} from '../../documentTemplate.constant'
import useDocumentTemplate from '../../useDocumentTemplate'

const TemplateDetailModal = ({ modalDetail, setModalDetail }) => {
  const { show, isCreate, detail } = modalDetail

  const { useCreateDocumentTemplate, useUpdateDocumentTemplate } = useDocumentTemplate()
  const createDocumentTemplate = useCreateDocumentTemplate()
  const updateDocumentTemplate = useUpdateDocumentTemplate()
  const methods = useForm({ defaultValues: detail })
  const dispatch = useDispatch()

  const type = useWatch({ control: methods.control, name: 'type' }) ?? DocumentTemplateType.WORK_INSTRUCTION

  useEffect(() => {
    methods.reset({ type: DocumentTemplateType.WORK_INSTRUCTION, ...detail })
  }, [detail, methods])

  const closeModal = () => {
    setModalDetail({ show: false, isCreate: true, detail: {} })
  }

  const onSubmit = async (data) => {
    const payload = {
      name: data.name,
      activeStartDate: data.activeStartDate,
      activeEndDate: data.activeEndDate || null,
      // `?? null` rather than leaving this `undefined`: an explicit `null` tells
      // the server to clear the reference when the file was removed in the
      // FileInput above; `undefined` would be dropped by JSON and leave the old
      // (now-deleted) FileStore id still referenced on the template.
      file: data.file?._id ?? null,
    }

    dispatch(showLoadingOverlay())
    try {
      if (isCreate) {
        await createDocumentTemplate.mutateAsync({
          ...payload,
          type: data.type,
          templateType: DocumentTemplateTemplateType.FILE,
        })
      } else {
        await updateDocumentTemplate.mutateAsync({ id: detail._id, data: payload })
      }
      closeModal()
    } catch (err) {
      toast.error(err.message)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={show} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent maxW="40%">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>
              <Heading>{isCreate ? 'Add' : 'Update'} Template</Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack>
                <Select name="type" label="Type" options={DOCUMENT_TEMPLATE_TYPE_OPTIONS} disabled={!isCreate} isRow />
                <Input name="name" label="Name" isRow />
                <FileInput
                  label="File"
                  name="file"
                  fileType="DOCUMENT_TEMPLATE_FILE"
                  accept={DOCUMENT_TEMPLATE_FILE_ACCEPT[type]}
                  canUpload
                  isRow
                />
                <DatePicker name="activeStartDate" label="Active Start Date (DD-MMM-YYYY)" isRow needPortal={false} />
                <DatePicker
                  name="activeEndDate"
                  label="Active End Date (DD-MMM-YYYY)"
                  isRow
                  isRequired={false}
                  needPortal={false}
                />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="green" mr={3} type="submit">
                {isCreate ? 'Add' : 'Update'}
              </Button>

              <Button colorScheme="gray" onClick={closeModal}>
                Close
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default TemplateDetailModal
